import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, DotsSixVertical } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import ChallengeListView from '@/components/admin/challenges/ChallengeListView';
import ChallengeDetailView from '@/components/admin/challenges/ChallengeDetailView';
import ParticipantNotesDialog from '@/components/admin/challenges/ParticipantNotesDialog';
import BroadcastModal from '@/components/admin/challenges/BroadcastModal';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

// Draggable goals list with inline edit (mantido no orquestrador — usado só na detail view).
function GoalsList({ goals, challengeId, onUpdate, onDelete }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = async (e, targetIdx) => {
    e.preventDefault(); setOverIdx(null);
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...goals];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx > dragIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setDragIdx(null);
    try { await axios.put(`${API}/api/challenges/${challengeId}/goals/reorder`, { ids: arr.map(g => g.id) }); onUpdate(); } catch { toast.error('Erro ao reordenar'); }
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const renameGoal = async (id, title) => { try { await axios.put(`${API}/api/goals/${id}`, { title }); onUpdate(); } catch {} };
  const FREQ_LABEL = { daily: 'diária', every_2_days: 'a cada 2 dias', weekly: 'semanal' };
  return (
    <div className="space-y-2">
      {goals.map((g, idx) => (
        <div key={g.id}
          draggable onDragStart={e => handleDragStart(e, idx)} onDragEnd={handleDragEnd}
          onDragOver={e => handleDragOver(e, idx)} onDrop={e => handleDrop(e, idx)}
          className={`flex items-center gap-2 p-2 bg-brand-bg/50 rounded-xl transition-all ${dragIdx === idx ? 'opacity-40' : ''} ${overIdx === idx && dragIdx !== idx ? 'ring-2 ring-brand-green/40 bg-brand-green/5' : ''}`}
          data-testid={`goal-${g.id}`}>
          <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green flex-shrink-0"><DotsSixVertical size={12} /></div>
          <div className="flex-1 min-w-0">
            <InlineEdit value={g.title} onSave={v => renameGoal(g.id, v)} className="text-sm text-brand-text" testId={`goal-title-${g.id}`} />
            <div className="flex gap-2 mt-0.5 flex-wrap items-center">
              <span className="text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">{g.category}</span>
              {g.target_type === 'quantitative' && <span className="text-[10px] text-brand-muted">Objetivo: {g.target_value}</span>}
              {g.recurrent && (
                <span className="text-[9px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid={`goal-recurrent-${g.id}`}>
                  recorrente · {FREQ_LABEL[g.frequency] || g.frequency} · dia {g.start_day || 1}{g.end_day ? `→${g.end_day}` : ''}
                </span>
              )}
            </div>
          </div>
          <button onClick={() => onDelete && onDelete(g)} className="text-brand-placeholder hover:text-red-500 p-1 flex-shrink-0" title="Eliminar meta" data-testid={`del-goal-${g.id}`}><Trash size={13} /></button>
        </div>
      ))}
    </div>
  );
}

/**
 * PROMPT 4 — Orquestrador. Toda a state e handlers permanecem aqui para garantir zero
 * regressões. As subviews (list / detail / dialogs) foram extraídas para
 * /components/admin/challenges/ — vê PROMPT 4 do PRD.
 */
export default function AdminDesafiosPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [challenges, setChallenges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', sigla: '', target_audience: '', visibility: 'public', start_date: '', end_date: '', ranking_type: 'adherence' });
  const [participants, setParticipants] = useState([]);
  const [notesParticipant, setNotesParticipant] = useState(null);
  const [notesList, setNotesList] = useState([]);
  const [editingNoteDate, setEditingNoteDate] = useState('');
  const [editingNoteValue, setEditingNoteValue] = useState('');
  const [clients, setClients] = useState([]);
  const [participantSearchResults, setParticipantSearchResults] = useState([]);
  const [participantSearchLoading, setParticipantSearchLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: '', category: 'alimentação', target_type: 'binary', target_value: '', recurrent: false, frequency: 'daily', start_day: 1, end_day: '' });
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [fileForm, setFileForm] = useState({ title: '', day: 1, file_type: 'pdf', file: null });
  const [dailyStats, setDailyStats] = useState([]);
  const [weeklyMood, setWeeklyMood] = useState(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ message: '', send_push: true, send_email: false });
  const [inactiveList, setInactiveList] = useState([]);
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState('');

  const fetchChallenges = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/challenges`); setChallenges(data); } catch {} finally { setLoading(false); }
  }, []);
  const fetchClients = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients?limit=50`); setClients(data); } catch {}
  }, []);
  useEffect(() => { fetchChallenges(); fetchClients(); }, [fetchChallenges, fetchClients]);

  // Adicionar participante: server-side debounced search.
  useEffect(() => {
    if (!showAddParticipant) return;
    const q = participantSearch.trim();
    if (!q) { setParticipantSearchResults([]); setParticipantSearchLoading(false); return; }
    setParticipantSearchLoading(true);
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/patients?search=${encodeURIComponent(q)}&limit=50`, { signal: ctrl.signal });
        setParticipantSearchResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') setParticipantSearchResults([]);
      } finally {
        setParticipantSearchLoading(false);
      }
    }, 250);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [participantSearch, showAddParticipant]);

  const openChallenge = async (c) => {
    setSelected(c);
    try {
      const [partsRes, statsRes, filesRes, dailyRes, inactRes] = await Promise.all([
        axios.get(`${API}/api/challenges/${c.id}/participants`),
        axios.get(`${API}/api/challenges/${c.id}/stats`),
        axios.get(`${API}/api/admin/challenges/${c.id}/files`),
        axios.get(`${API}/api/admin/challenges/${c.id}/daily-stats`),
        axios.get(`${API}/api/admin/challenges/${c.id}/inactive?days=3`),
      ]);
      setParticipants(partsRes.data);
      setStats(statsRes.data);
      setFiles(Array.isArray(filesRes.data) ? filesRes.data : []);
      setDailyStats(dailyRes.data?.days || []);
      setInactiveList(Array.isArray(inactRes.data) ? inactRes.data : []);
      try { const { data: wm } = await axios.get(`${API}/api/challenges/${c.id}/weekly-mood`); setWeeklyMood(wm); } catch { setWeeklyMood(null); }
      if (partsRes.data.length > 0) {
        const { data: pg } = await axios.get(`${API}/api/patients/${partsRes.data[0].patient_id}/goals-list`);
        setGoals(pg.filter(g => g.challenge_id === c.id));
      } else {
        setGoals([]);
      }
    } catch {}
  };

  const handleCreate = async () => {
    try {
      const { data } = await axios.post(`${API}/api/challenges`, form);
      toast.success('Desafio criado'); setShowCreate(false); setForm({ title: '', description: '', sigla: '', target_audience: '', visibility: 'public', start_date: '', end_date: '', ranking_type: 'adherence' });
      fetchChallenges(); openChallenge(data);
    } catch { toast.error('Erro'); }
  };
  const handleDelete = async (id) => {
    const c = challenges.find(x => x.id === id) || selected;
    const ok = await confirmDelete({ description: `Vai apagar o desafio "${c?.title || ''}" com todos os participantes, metas e ficheiros.` });
    if (!ok) return;
    await axios.delete(`${API}/api/challenges/${id}`); toast.success('Eliminado'); setSelected(null); fetchChallenges();
  };
  const openDuplicateDialog = () => { setDuplicateDate(new Date().toISOString().split('T')[0]); setShowDuplicate(true); };
  const handleDuplicate = async () => {
    if (!selected || !duplicateDate) { toast.error('Indica a nova data de início'); return; }
    try {
      const { data } = await axios.post(`${API}/api/challenges/${selected.id}/duplicate`, { new_start_date: duplicateDate });
      toast.success('Desafio duplicado');
      setShowDuplicate(false);
      fetchChallenges();
      openChallenge(data);
    } catch { toast.error('Erro ao duplicar'); }
  };
  const handleDeleteGoal = async (g) => {
    const ok = await confirmDelete({ description: `Vai apagar a meta "${g.title}". Esta ação remove-a de todos os participantes.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/admin/challenges/${selected.id}/goals/${g.id}`);
      toast.success('Meta eliminada');
      openChallenge(selected);
    } catch { toast.error('Erro'); }
  };
  const handleFileUpload = async () => {
    if (!fileForm.file) { toast.error('Selecciona um ficheiro'); return; }
    try {
      const fd = new FormData();
      fd.append('file', fileForm.file);
      fd.append('day', String(fileForm.day));
      fd.append('title', fileForm.title || fileForm.file.name);
      fd.append('file_type', fileForm.file_type);
      await axios.post(`${API}/api/admin/challenges/${selected.id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Ficheiro carregado');
      setShowFileUpload(false);
      setFileForm({ title: '', day: 1, file_type: 'pdf', file: null });
      openChallenge(selected);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar'); }
  };
  const handleDeleteFile = async (fid) => {
    const f = files.find(x => x.id === fid);
    const ok = await confirmDelete({ description: `Vai apagar o ficheiro "${f?.title || ''}".` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/admin/challenges/${selected.id}/files/${fid}`);
      toast.success('Ficheiro eliminado');
      openChallenge(selected);
    } catch { toast.error('Erro'); }
  };
  const handleBroadcast = async () => {
    if (!broadcastForm.message.trim()) { toast.error('Mensagem vazia'); return; }
    try {
      const { data } = await axios.post(`${API}/api/admin/challenges/${selected.id}/broadcast`, broadcastForm);
      toast.success(`Enviado a ${data.recipients} participantes (${data.sent_push} push, ${data.sent_email} email)`);
      setShowBroadcast(false);
      setBroadcastForm({ message: '', send_push: true, send_email: false });
    } catch { toast.error('Erro a enviar'); }
  };
  const handleAddParticipant = async (patientId) => {
    try { await axios.post(`${API}/api/challenges/${selected.id}/participants`, { patient_id: patientId }); toast.success('Participante adicionado'); openChallenge(selected); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const handleRemoveParticipant = async (patientId) => {
    const p = participants.find(x => x.patient_id === patientId);
    const ok = await confirmDelete({ description: `Vai remover ${p?.patient_name || 'esta participante'} do desafio.` });
    if (!ok) return;
    await axios.delete(`${API}/api/challenges/${selected.id}/participants/${patientId}`); toast.success('Removido'); openChallenge(selected);
  };

  // Carregar feelings de uma participante quando o dialog abre.
  useEffect(() => {
    if (!notesParticipant || !selected) { setNotesList([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/challenges/${selected.id}/participants/${notesParticipant.patient_id}/feelings`);
        setNotesList(Array.isArray(data) ? data : []);
        // Notes for this patient are now auto-marked read server-side — refresh the sidebar badge
        window.dispatchEvent(new Event('nav-badges-refresh'));
      } catch { setNotesList([]); }
    })();
  }, [notesParticipant, selected]);

  const saveNutriNote = async (date, value) => {
    if (!selected || !notesParticipant) return;
    try {
      await axios.put(`${API}/api/admin/challenges/${selected.id}/participants/${notesParticipant.patient_id}/nutri-note`, { date, nutri_note: value });
      toast.success('Nota da nutri guardada');
      setEditingNoteDate(''); setEditingNoteValue('');
      const { data } = await axios.get(`${API}/api/challenges/${selected.id}/participants/${notesParticipant.patient_id}/feelings`);
      setNotesList(Array.isArray(data) ? data : []);
    } catch { toast.error('Erro a guardar'); }
  };
  const handleAddGoal = async () => {
    try {
      const payload = { ...goalForm };
      if (!payload.recurrent) {
        delete payload.start_day; delete payload.end_day; delete payload.frequency;
      } else if (payload.end_day === '') {
        payload.end_day = null;
      }
      await axios.post(`${API}/api/challenges/${selected.id}/goals`, payload);
      toast.success('Meta criada para todos os participantes'); setShowAddGoal(false);
      setGoalForm({ title: '', category: 'alimentação', target_type: 'binary', target_value: '', recurrent: false, frequency: 'daily', start_day: 1, end_day: '' });
      openChallenge(selected);
    } catch { toast.error('Erro'); }
  };
  const handleToggle = async () => {
    await axios.put(`${API}/api/challenges/${selected.id}`, { active: !selected.active });
    const updated = { ...selected, active: !selected.active };
    setSelected(updated); fetchChallenges();
    toast.success(updated.active ? 'Desafio ativado' : 'Desafio desativado');
  };
  const handleRankingTypeChange = async (v) => {
    try {
      await axios.put(`${API}/api/challenges/${selected.id}`, { ranking_type: v });
      const updated = { ...selected, ranking_type: v };
      setSelected(updated); fetchChallenges();
      const { data } = await axios.get(`${API}/api/challenges/${selected.id}/stats`);
      setStats(data);
      toast.success('Tipo de ranking atualizado');
    } catch { toast.error('Erro'); }
  };
  const handleVisibilityChange = async (v) => {
    try {
      await axios.put(`${API}/api/challenges/${selected.id}`, { visibility: v });
      const updated = { ...selected, visibility: v };
      setSelected(updated); fetchChallenges();
      toast.success(v === 'public' ? 'Desafio agora é público (visível no catálogo)' : 'Desafio agora é silencioso (só gestão)');
    } catch { toast.error('Erro'); }
  };

  const closeNotes = () => { setNotesParticipant(null); setEditingNoteDate(''); setEditingNoteValue(''); };

  return (
    <>
      {selected ? (
        <ChallengeDetailView
          selected={selected}
          participants={participants}
          goals={goals}
          stats={stats}
          files={files}
          dailyStats={dailyStats}
          weeklyMood={weeklyMood}
          inactiveList={inactiveList}
          showAddGoal={showAddGoal}
          setShowAddGoal={setShowAddGoal}
          goalForm={goalForm}
          setGoalForm={setGoalForm}
          showAddParticipant={showAddParticipant}
          setShowAddParticipant={setShowAddParticipant}
          participantSearch={participantSearch}
          setParticipantSearch={setParticipantSearch}
          participantSearchResults={participantSearchResults}
          participantSearchLoading={participantSearchLoading}
          clients={clients}
          showFileUpload={showFileUpload}
          setShowFileUpload={setShowFileUpload}
          fileForm={fileForm}
          setFileForm={setFileForm}
          onBack={() => setSelected(null)}
          onToggle={handleToggle}
          onOpenBroadcast={() => setShowBroadcast(true)}
          onOpenDuplicate={openDuplicateDialog}
          onDelete={handleDelete}
          onAddParticipant={handleAddParticipant}
          onRemoveParticipant={handleRemoveParticipant}
          onOpenNotes={setNotesParticipant}
          onAddGoal={handleAddGoal}
          onDeleteGoal={handleDeleteGoal}
          GoalsListComp={GoalsList}
          onChallengeRefresh={() => openChallenge(selected)}
          onRankingTypeChange={handleRankingTypeChange}
          onVisibilityChange={handleVisibilityChange}
          onFileUpload={handleFileUpload}
          onDeleteFile={handleDeleteFile}
        />
      ) : (
        <ChallengeListView
          challenges={challenges}
          loading={loading}
          onSelect={openChallenge}
          onOpenCreate={() => setShowCreate(true)}
          onDuplicateFromList={(c) => { setSelected(c); openDuplicateDialog(); }}
        />
      )}

      {/* Create dialog (mantido no orquestrador) */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo Desafio</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Criar um novo desafio de grupo com visibilidade, público-alvo e tipo de ranking.</DialogDescription>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: 7 dias mais estruturados" className="rounded-xl border-brand-border" required data-testid="challenge-title" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Descrição</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-xl border-brand-border" rows={2} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Público-alvo (opcional)</Label><Input value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} placeholder="Ex: Ideal para grávidas" className="rounded-xl border-brand-border" data-testid="challenge-target-audience" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Início</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="rounded-xl border-brand-border" required /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Fim</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="rounded-xl border-brand-border" required /></div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Visibilidade</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, visibility: 'public' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${form.visibility === 'public' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="create-visibility-public">Público (catálogo)</button>
                <button type="button" onClick={() => setForm({ ...form, visibility: 'silent' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${form.visibility === 'silent' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid="create-visibility-silent">Silencioso (só gestão)</button>
              </div>
              <p className="text-[10px] text-brand-placeholder mt-1">Público aparece no catálogo para auto-inscrição. Silencioso só é gerido aqui.</p>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Tipo de ranking</Label>
              <select value={form.ranking_type} onChange={e => setForm({ ...form, ranking_type: e.target.value })} className="w-full px-3 py-2 border border-brand-border rounded-xl text-sm bg-white" data-testid="create-ranking-type">
                <option value="adherence">Adesão</option>
                <option value="weight_loss">Perda de peso</option>
                <option value="none">Sem ranking</option>
              </select>
            </div>
            <button onClick={handleCreate} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="submit-challenge">Criar desafio</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate modal */}
      <Dialog open={showDuplicate} onOpenChange={setShowDuplicate}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="duplicate-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Duplicar desafio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">Vai criar uma cópia de &quot;{selected?.title || ''}&quot; com todas as metas e ficheiros (mas sem participantes).</p>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Nova data de início</Label>
              <Input type="date" value={duplicateDate} onChange={e => setDuplicateDate(e.target.value)} className="rounded-xl border-brand-border" data-testid="duplicate-date-input" />
              <p className="text-[10px] text-brand-placeholder mt-1">A data de fim será calculada automaticamente preservando a duração do desafio original.</p>
            </div>
            <button onClick={handleDuplicate} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="duplicate-confirm-btn">Duplicar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast modal (single instance — extraído) */}
      <BroadcastModal
        open={showBroadcast && !!selected}
        onOpenChange={setShowBroadcast}
        challengeTitle={selected?.title || ''}
        form={broadcastForm}
        setForm={setBroadcastForm}
        onSend={handleBroadcast}
      />

      {/* Participant notes dialog — ÚNICA INSTÂNCIA (bug do duplicado corrigido) */}
      <ParticipantNotesDialog
        open={!!notesParticipant}
        onClose={closeNotes}
        participantName={notesParticipant?.patient_name || ''}
        notesList={notesList}
        editingNoteDate={editingNoteDate}
        setEditingNoteDate={setEditingNoteDate}
        editingNoteValue={editingNoteValue}
        setEditingNoteValue={setEditingNoteValue}
        onSaveNutriNote={saveNutriNote}
      />
    </>
  );
}
